// sort-imports-ignore
// Must stay first: removes the hand-off params before anything records the URL.
import "./stripAuthHandoff.ts";
import "./utils/Sentry.ts";
import "./i18n";
// Chart setup (module registration + global defaults) must run before any
// lazily-loaded route can render a chart — see the module for details.
import "./components/charts/setupHighcharts.ts";
// dayjs plugin + locale registration (INS-1611) — belt-and-braces eager
// import so no route can format a date before plugins are registered; app
// code imports the configured instance from ~/utils/dayjs.
import "~/utils/dayjs";

import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import App from "./App.tsx";
import { Env } from "./utils/Env.ts";
import { suppressReportsUntilReload } from "./utils/Sentry.ts";

import "./index.css";

const PRELOAD_RELOAD_AT_KEY = "vite-preload-reload-at";
const PRELOAD_RELOAD_COOLDOWN_MS = 10_000;

// preventDefault() stops Vite rethrowing the import error, but the lazy import
// then resolves undefined and the route error boundary still fires before the
// reload lands, so Sentry reports are suppressed until then. A failure again
// within the cooldown is rethrown instead of reloading in a loop.
window.addEventListener("vite:preloadError", (event) => {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(PRELOAD_RELOAD_AT_KEY)) || 0;
    sessionStorage.setItem(PRELOAD_RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: no loop guard, so let the error surface.
    return;
  }
  if (Date.now() - lastReloadAt < PRELOAD_RELOAD_COOLDOWN_MS) return;

  event.preventDefault();
  suppressReportsUntilReload();
  console.info("Chunk load error detected. Reloading page...");
  window.location.reload();
});

let appComponent = <App />;

// Map WorkOS organization IDs to demo scenarios
const ORGANIZATION_TO_SCENARIO: Record<string, string> = {
  org_01K9RNYV2W6AAMY27TCN3B313B: "DemoAus",
  org_01K9RNZV8B5B65XS3PN694B0SF: "DemoUSA",
};

async function enableMocking() {
  if (Env.VITE_MOCK !== "true") {
    return;
  }

  const { AuthProvider } = await import("./providers/auth");
  const { MAIN_ENTRY_PAGE } = await import("./utils/Constants");

  appComponent = (
    <AuthProvider
      onRedirectCallback={({ user, organizationId }) => {
        // Store user info in localStorage for MSW
        localStorage.setItem(
          "MEQ-Mock-userInfo",
          JSON.stringify({
            email: user.email,
            name:
              user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : user.email,
          }),
        );

        // Map organizationId to demo scenario
        const demoScenario =
          (organizationId && ORGANIZATION_TO_SCENARIO[organizationId]) ||
          Env.VITE_MOCK_SCENARIO ||
          "DemoUSA";

        localStorage.setItem("MEQ-Mock-scenario", demoScenario);

        // Redirect to main entry page like before
        window.location.replace(MAIN_ENTRY_PAGE);
      }}
    >
      <App />
    </AuthProvider>
  );

  const { loadScenario } = await import("./mocks/browser");
  await loadScenario();
}

enableMocking().then(() => {
  createRoot(document.getElementById("root")!, {
    // Errors stopped by an error boundary never reach window.onerror, so
    // Sentry's global handler misses them. Overriding this drops React's
    // default console logging, hence the callback.
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) =>
      console.error(error, errorInfo.componentStack),
    ),
  }).render(<StrictMode>{appComponent}</StrictMode>);
});
